<template>
  <button
    type="button"
    class="wizard-back-rail"
    :aria-label="ariaLabel"
    @click="$emit('back')"
  >
    <!-- chevron + 步骤轨作为一组，在侧栏内垂直居中 -->
    <span class="wizard-back-rail__stack" aria-hidden="true">
      <span class="wizard-back-rail__chevronZone">
        <span class="wizard-back-rail__chevron">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M14.5 6.5L9 12l5.5 5.5"
              stroke="currentColor"
              stroke-width="1.75"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </span>
      </span>

      <span
        v-if="steps.length"
        class="wizard-back-rail__track"
      >
        <span
          v-for="(step, index) in steps"
          :key="step.id || step.label || index"
          class="wizard-back-rail__trackItem"
          :class="`wizard-back-rail__trackItem--${step.status}`"
        >
          <span class="wizard-back-rail__trackNode">
            <span class="wizard-back-rail__dot" />
          </span>
          <span class="wizard-back-rail__trackLabel">{{ step.label }}</span>
          <span
            v-if="index < steps.length - 1"
            class="wizard-back-rail__trackLine"
          />
        </span>
      </span>
    </span>
  </button>
</template>

<script>
/**
 * 向导左栏返回轨：铺满侧栏全宽全高，内容块垂直居中。
 * 与 batch-tag-dialog__methodSidebar 配合，侧栏背景由外层容器承担。
 */
export default {
  name: 'WizardBackRail',
  props: {
    ariaLabel: {
      type: String,
      default: '返回上一步',
    },
    /**
     * 纵向步骤：{ label, status: 'done'|'active'|'pending', id? }
     */
    steps: {
      type: Array,
      default: () => [],
    },
  },
}
</script>

<style scoped>
.wizard-back-rail {
  position: relative;
  flex: 1 1 auto;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 100%;
  margin: 0;
  padding: 20px 0;
  border: none;
  background: transparent;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.28s cubic-bezier(0.22, 1, 0.36, 1);
}

.wizard-back-rail__stack {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 28px;
  flex-shrink: 0;
}

.wizard-back-rail:hover {
  background: rgba(68, 64, 60, 0.05);
}

.wizard-back-rail:active {
  background: rgba(68, 64, 60, 0.08);
}

.wizard-back-rail:focus-visible {
  outline: none;
  box-shadow: inset 0 0 0 2px rgba(168, 162, 158, 0.55);
}

.wizard-back-rail__chevronZone {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  width: 100%;
}

.wizard-back-rail__chevron {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--link-ink-muted, #a8a29e);
  transform: translateX(1px);
  transition:
    transform 0.32s cubic-bezier(0.22, 1, 0.36, 1),
    color 0.22s ease;
}

.wizard-back-rail__chevron svg {
  width: 32px;
  height: 32px;
  display: block;
}

.wizard-back-rail:hover .wizard-back-rail__chevron {
  color: var(--link-ink-emphasis, #44403c);
  transform: translateX(-6px);
}

.wizard-back-rail:active .wizard-back-rail__chevron {
  transform: translateX(-4px);
}

.wizard-back-rail__track {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  flex-shrink: 0;
}

.wizard-back-rail__trackItem {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.wizard-back-rail__trackNode {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
}

.wizard-back-rail__dot {
  display: block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--link-border-soft, #e7e5e4);
  transition:
    transform 0.22s ease,
    background 0.22s ease,
    box-shadow 0.22s ease;
}

.wizard-back-rail__trackItem--done .wizard-back-rail__dot {
  background: var(--link-ink-muted, #a8a29e);
}

.wizard-back-rail__trackItem--active .wizard-back-rail__dot {
  width: 9px;
  height: 9px;
  background: var(--link-ink-emphasis, #44403c);
  box-shadow: 0 0 0 3px rgba(68, 64, 60, 0.1);
}

.wizard-back-rail__trackItem--pending .wizard-back-rail__dot {
  background: transparent;
  border: 1.5px solid var(--link-border-soft, #e7e5e4);
  width: 7px;
  height: 7px;
}

.wizard-back-rail__trackLabel {
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.04em;
  line-height: 1.2;
  color: var(--link-ink-faint, #d6d3d1);
  white-space: nowrap;
}

.wizard-back-rail__trackItem--done .wizard-back-rail__trackLabel {
  color: var(--link-ink-muted, #a8a29e);
}

.wizard-back-rail__trackItem--active .wizard-back-rail__trackLabel {
  color: var(--link-ink-emphasis, #44403c);
  font-weight: 600;
}

.wizard-back-rail__trackItem--pending .wizard-back-rail__trackLabel {
  color: var(--link-ink-faint, #d6d3d1);
}

.wizard-back-rail__trackLine {
  display: block;
  width: 1px;
  height: 24px;
  margin: 6px 0;
  background: linear-gradient(
    180deg,
    rgba(231, 229, 228, 0.9) 0%,
    rgba(231, 229, 228, 0.35) 100%
  );
}

.wizard-back-rail__trackItem--done .wizard-back-rail__trackLine {
  background: linear-gradient(
    180deg,
    rgba(168, 162, 158, 0.55) 0%,
    rgba(231, 229, 228, 0.4) 100%
  );
}

.wizard-back-rail:hover .wizard-back-rail__trackItem--active .wizard-back-rail__dot {
  transform: scale(1.06);
}
</style>
